import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Clock, Play, Zap, Target, Trophy, Beaker, FlaskConical, Search } from 'lucide-react';
import type { ModeMetadata } from '../modes';
import { DjayAvatar, useDjaySelection, getDjayModeIntro } from '../mascot';
interface LabStationProps {
  mode: ModeMetadata;
  personalNote?: string;
  masteryPercent?: number;
  statLabel?: string;
  personalRecord?: string;
  onClick: () => void;
}
const iconMap = {
  Zap,
  Target,
  Trophy,
  Beaker,
  FlaskConical,
  Search
};
const colorMap: Record<string, {
  bg: string;
  text: string;
  border: string;
  gradient: string;
  shadow: string;
}> = {
  Zap: {
    bg: 'bg-amber-100',
    text: 'text-amber-600',
    border: 'border-amber-200',
    gradient: 'from-amber-400 to-orange-500',
    shadow: 'shadow-amber-200'
  },
  Target: {
    bg: 'bg-indigo-100',
    text: 'text-indigo-600',
    border: 'border-indigo-200',
    gradient: 'from-indigo-400 to-purple-500',
    shadow: 'shadow-indigo-200'
  },
  Trophy: {
    bg: 'bg-violet-100',
    text: 'text-violet-600',
    border: 'border-violet-200',
    gradient: 'from-violet-400 to-fuchsia-500',
    shadow: 'shadow-violet-200'
  },
  Beaker: {
    bg: 'bg-teal-100',
    text: 'text-teal-600',
    border: 'border-teal-200',
    gradient: 'from-teal-400 to-emerald-500',
    shadow: 'shadow-teal-200'
  },
  FlaskConical: {
    bg: 'bg-rose-100',
    text: 'text-rose-600',
    border: 'border-rose-200',
    gradient: 'from-rose-400 to-pink-500',
    shadow: 'shadow-rose-200'
  },
  Search: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-600',
    border: 'border-emerald-200',
    gradient: 'from-emerald-400 to-teal-500',
    shadow: 'shadow-emerald-200'
  }
};
const ctaTextMap: Record<string, string> = {
  fluency: 'Train snelheid',
  sprint: 'Start sprint',
  challenge: 'Accepteer uitdaging',
  free: 'Kies onderwerp'
};
/**
 * LabStation — Interactive card for a single practice mode.
 *
 * Shows mode icon, title, description, personalized note, and an inline Djay message.
 * Part of the learningLab domain (DDD co-locatie s57).
 */
export function LabStation({
  mode,
  personalNote,
  masteryPercent,
  statLabel,
  personalRecord,
  onClick
}: LabStationProps) {
  const Icon = iconMap[mode.icon as keyof typeof iconMap] || Beaker;
  const colors = colorMap[mode.icon] || colorMap.Beaker;
  const isDisabled = !mode.isEnabled;
  const {
    selectedVariant
  } = useDjaySelection();
  const modeIntro = useMemo(() => getDjayModeIntro(mode.id), [mode.id]);
  const ctaText = ctaTextMap[mode.id] || 'Start';
  return <motion.div whileHover={!isDisabled ? {
    y: -6,
    scale: 1.02
  } : {}} whileTap={!isDisabled ? {
    scale: 0.96
  } : {}} transition={{
    type: 'spring',
    stiffness: 400,
    damping: 25
  }} className="h-full relative" data-id="element-2803">
      <div className={`h-full flex flex-col rounded-[2rem] transition-all relative overflow-hidden group
          ${isDisabled ? 'bg-slate-100 opacity-60 cursor-not-allowed' : `bg-gradient-to-br ${colors.gradient} cursor-pointer hover:shadow-xl shadow-lg`}`} onClick={!isDisabled ? onClick : undefined} data-id="element-2804">
        <div className={`p-6 flex flex-col h-full relative z-10 ${isDisabled ? 'text-slate-500' : 'text-white'}`} data-id="element-2805">
          {/* Top Row: Icon & Time */}
          <div className="flex items-start justify-between mb-4" data-id="element-2806">
            <motion.div whileHover={!isDisabled ? {
            rotate: [0, -10, 10, -10, 0]
          } : {}} transition={{
            duration: 0.5
          }} className={`w-14 h-14 rounded-2xl flex items-center justify-center backdrop-blur-sm
                ${isDisabled ? 'bg-slate-200' : 'bg-white/20 shadow-inner'}`} data-id="element-2807">
              <Icon className={`w-7 h-7 ${isDisabled ? 'text-slate-400' : 'text-white'}`} strokeWidth={2.5} data-id="element-2808" />
            </motion.div>

            <div className={`flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full backdrop-blur-sm
              ${isDisabled ? 'bg-slate-200 text-slate-500' : 'bg-white/20 text-white'}`} data-id="element-2809">
              <Clock className="w-4 h-4" data-id="element-2810" />
              <span data-id="element-2811">{mode.estimatedMinutes}m</span>
            </div>
          </div>

          {/* Title & Subtitle */}
          <div className="mb-4" data-id="element-2812">
            <h3 className="text-2xl font-black tracking-tight leading-tight mb-1" data-id="element-2813">
              {mode.title}
            </h3>
            <p className={`text-sm font-medium line-clamp-2 ${isDisabled ? 'text-slate-400' : 'text-white/90'}`} data-id="element-2814">
              {mode.description}
            </p>
            {personalRecord && !isDisabled && <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/10 text-xs font-bold text-white/90" data-id="element-2815">
                <Trophy className="w-3.5 h-3.5" data-id="element-2816" />
                {personalRecord}
              </div>}
          </div>

          {/* Djay Message Inline */}
          <div className={`rounded-xl p-3 flex items-center gap-3 mb-4 mt-auto
            ${isDisabled ? 'bg-slate-200/50' : 'bg-white/20 backdrop-blur-sm'}`} data-id="element-2817">
            <div className="flex-shrink-0" data-id="element-2818">
              <DjayAvatar size="sm" emotion={modeIntro.emotion} variant={selectedVariant} animate={false} data-id="element-2819" />
            </div>
            <p className="text-xs font-bold leading-snug" data-id="element-2820">{modeIntro.text}</p>
          </div>

          {/* Progress Bar */}
          {masteryPercent !== undefined && !isDisabled && <div className="mb-4" data-id="element-2821">
              <div className="flex justify-between items-center mb-1.5" data-id="element-2822">
                <span className="text-[10px] font-bold uppercase tracking-wider text-white/80" data-id="element-2823">
                  Voortgang
                </span>
                <span className="text-[10px] font-bold text-white" data-id="element-2824">
                  {Math.round(masteryPercent)}%
                </span>
              </div>
              <div className="h-1.5 w-full bg-black/10 rounded-full overflow-hidden" data-id="element-2825">
                <motion.div className="h-full bg-white rounded-full" initial={{
              width: 0
            }} animate={{
              width: `${Math.max(0, Math.min(100, masteryPercent))}%`
            }} transition={{
              duration: 1,
              ease: 'easeOut'
            }} data-id="element-2826" />
              </div>
            </div>}

          {/* Action Area */}
          <div className={`pt-4 flex items-center justify-between border-t ${isDisabled ? 'border-slate-200' : 'border-white/20'}`} data-id="element-2827">
            <div className={`text-xs font-bold ${isDisabled ? 'text-slate-400' : 'text-white/90'} line-clamp-1 pr-2`} data-id="element-2828">
              {statLabel || personalNote || mode.tags[0]}
            </div>

            {!isDisabled ? <div className={`px-4 py-2 rounded-xl bg-white text-sm font-black flex items-center gap-2 transition-transform group-hover:scale-105 shadow-sm ${colors.text}`} data-id="element-2829">
                {ctaText}
                <Play className="w-4 h-4" fill="currentColor" data-id="element-2830" />
              </div> : <span className="text-xs font-black bg-slate-200 text-slate-400 px-4 py-2 rounded-xl" data-id="element-2831">
                Binnenkort
              </span>}
          </div>
        </div>
      </div>
    </motion.div>;
}